import type { SettingsPage } from '@features/settings/api/contracts'
import type { ModuleSlug } from '@features/settings/models'
import {
  AutomationsIcon,
  BuildingIcon,
  CalendarIcon,
  ColumnsIcon,
  DatabaseIcon,
  GearIcon,
  InfoIcon,
  KeyIcon,
  MailIcon,
  IntegrationsIcon,
  PlayIcon,
  PowerIcon,
  RecordFolderIcon,
  SlidersIcon,
  SparkleChatIcon,
  UserIcon,
  UsersIcon,
} from '../features/shared/icons/icons'
import type { IconProps } from '../features/shared/icons/icons'
import { Path } from '@/routes/paths'

export const APP_MODULE_IDS = [
  'arvoke-ai',
  'tables',
  'automations',
  'integrations',
  'settings',
  'users',
  'me',
] as const

export type AppModuleId = (typeof APP_MODULE_IDS)[number]

export type NavigationIcon = (props: IconProps) => JSX.Element

export interface AppNavigationItem {
  label: string
  to: string
  /** One sentence on what the page is, behind its info mark in the menu. */
  description?: string
  icon?: NavigationIcon
  /** Hidden items are excluded from both navigation and page search. */
  visible?: boolean
  /** Visible destinations are searchable unless explicitly opted out. */
  searchable?: boolean
}

export interface AppNavigationSection {
  label: string
  items: readonly AppNavigationItem[]
}

/**
 * Where a module sits on the rail: the work areas at the top, then at its
 * foot the configuration areas and, below them, the signed-in user's own.
 */
export type AppRailGroup = 'work' | 'configuration' | 'account'

export interface AppNavigationModule {
  id: AppModuleId
  label: string
  /** One sentence on what the area is, behind its info mark on the rail. */
  description?: string
  pathPrefix: string
  icon: NavigationIcon
  drawer: 'none' | 'sections' | 'table-tree' | 'chat-history'
  railGroup: AppRailGroup
  /**
   * The catalog module it belongs to (docs/specs/MODULES.md); the account area
   * has none. The catalog says whether the tenant has it and which permission
   * module, if any, lets a person open it (`getModulePermissionKey`).
   */
  moduleSlug?: ModuleSlug
  sections: readonly AppNavigationSection[]
}

export interface SearchableAppPage {
  id: string
  name: string
  module: string
  path: string
}

const ARVOKE_AI_MODULE: AppNavigationModule = {
  id: 'arvoke-ai',
  label: 'AI Assistant',
  description: 'Ask the AI Assistant about your data and the app.',
  pathPrefix: Path.aiChat,
  icon: SparkleChatIcon,
  // The drawer lists the user's own conversations (decision 18).
  drawer: 'chat-history',
  railGroup: 'work',
  moduleSlug: 'ai',
  sections: [
    {
      label: 'AI Assistant',
      items: [{ label: 'AI Assistant', to: Path.aiChat }],
    },
  ],
}

const TABLES_MODULE: AppNavigationModule = {
  id: 'tables',
  label: 'Tables',
  description: "Your organization's tables and their views.",
  pathPrefix: '/tables',
  icon: DatabaseIcon,
  drawer: 'table-tree',
  railGroup: 'work',
  moduleSlug: 'data',
  sections: [
    {
      label: 'Tables',
      items: [{ label: 'Tables', to: Path.tables }],
    },
  ],
}

const AUTOMATIONS_MODULE: AppNavigationModule = {
  id: 'automations',
  label: 'Automations',
  description: 'Workflows that run on a schedule or by hand.',
  pathPrefix: Path.automationsRoot,
  icon: AutomationsIcon,
  drawer: 'sections',
  railGroup: 'work',
  moduleSlug: 'automations',
  sections: [
    {
      label: 'Automations',
      items: [
        {
          label: 'Overview',
          to: Path.automationsOverview,
          icon: InfoIcon,
          description: 'Counts, run activity, upcoming and recent runs.',
        },
        {
          label: 'Automations',
          to: Path.automations,
          icon: AutomationsIcon,
          description: 'Every automation, where you add new ones.',
        },
        {
          label: 'Runs',
          to: Path.automationRuns,
          icon: PlayIcon,
          description: 'Every run of every automation.',
        },
      ],
    },
  ],
}

const INTEGRATIONS_MODULE: AppNavigationModule = {
  id: 'integrations',
  label: 'Integrations',
  description: 'Bring tables and columns in from external systems.',
  pathPrefix: Path.integrationsRoot,
  icon: IntegrationsIcon,
  drawer: 'sections',
  railGroup: 'work',
  moduleSlug: 'integrations',
  sections: [
    {
      label: 'Integrations',
      items: [
        {
          label: 'Overview',
          to: Path.integrationsOverview,
          icon: InfoIcon,
          description: 'Health, totals, problems and recent runs.',
        },
        {
          label: 'Sources',
          to: Path.integrationsSources,
          icon: DatabaseIcon,
          description: 'The external systems Arvoke can pull from.',
        },
        {
          label: 'External Tables',
          to: Path.integrationsTables,
          icon: RecordFolderIcon,
          description: 'Every table from every source, edited in place.',
        },
        {
          label: 'External Columns',
          to: Path.integrationsFields,
          icon: ColumnsIcon,
          description: 'Every column from every source, edited in place.',
        },
        {
          label: 'Runs',
          to: Path.integrationsRuns,
          icon: PlayIcon,
          description: 'The history of integration runs.',
        },
      ],
    },
  ],
}

const USERS_MODULE: AppNavigationModule = {
  id: 'users',
  label: 'Users',
  description: 'People, their permission groups and preference defaults.',
  pathPrefix: Path.usersRoot,
  icon: UsersIcon,
  drawer: 'sections',
  railGroup: 'configuration',
  moduleSlug: 'users',
  sections: [
    {
      label: 'Directory',
      items: [
        {
          label: 'Users',
          to: Path.users,
          icon: UserIcon,
          description: 'Add and invite people, and edit their details and groups.',
        },
        {
          label: 'Permission Groups',
          to: Path.usersPermissionGroups,
          icon: KeyIcon,
          description: 'Which tables, fields and admin areas each group can use.',
        },
        {
          label: 'Preference Groups',
          to: Path.usersPreferenceGroups,
          icon: UsersIcon,
          description: 'Preference values for the members of one group.',
        },
        {
          label: 'Preference Defaults',
          to: Path.usersPreferenceDefaults,
          icon: SlidersIcon,
          description: "Your organization's preference values for everyone.",
        },
      ],
    },
  ],
}

// The rail draws this one with the user's photo or initials instead of its icon.
const ME_MODULE: AppNavigationModule = {
  id: 'me',
  label: 'Me',
  description: 'Your profile and preferences.',
  pathPrefix: '/me',
  icon: UserIcon,
  drawer: 'sections',
  railGroup: 'account',
  sections: [
    {
      label: 'Me',
      items: [
        {
          label: 'Profile',
          to: Path.profile,
          icon: UserIcon,
          description: 'Your name, photo and password.',
        },
        {
          label: 'Preferences',
          to: Path.preferences,
          icon: SlidersIcon,
          description: 'How Arvoke looks and behaves for you.',
        },
      ],
    },
  ],
}

/** A settings page's icon in the Settings menu, and so beside its title. */
export function getSettingsPageIcon(pageKey: string): NavigationIcon | undefined {
  switch (pageKey) {
    case 'organization':
      return BuildingIcon
    case 'modules':
      return PowerIcon
    case 'automations':
      return AutomationsIcon
    case 'email':
      return MailIcon
    case 'ai':
      return SparkleChatIcon
    default:
      return undefined
  }
}

/** Settings pages that belong to a module: each shows only while it's on. */
const MODULE_SETTINGS_PAGES: Readonly<Record<string, ModuleSlug>> = {
  automations: 'automations',
}

function buildSettingsModule(
  settingsPages: readonly SettingsPage[],
  credentialsAvailable = true,
  enabledModuleSlugs: ReadonlySet<string> | null = null
): AppNavigationModule {
  const moduleIsOn = (pageKey: string) => {
    const slug = MODULE_SETTINGS_PAGES[pageKey]
    return slug === undefined || (enabledModuleSlugs !== null && enabledModuleSlugs.has(slug))
  }
  const pageItems = settingsPages
    .filter((page) => page.page_key !== 'credentials')
    .map((page) => ({
      label: page.page_label,
      to: `/settings/${encodeURIComponent(page.page_key)}`,
      description: page.page_description ?? undefined,
      icon: getSettingsPageIcon(page.page_key),
      // Preferences are edited under Me, not the Settings drawer.
      visible: page.is_active && page.page_key !== 'preferences' && moduleIsOn(page.page_key),
    }))
  // Periods isn't a catalog page (its rules live behind /periods): it joins
  // the catalog's pages right after Organization.
  const organization = pageItems.findIndex((item) => item.to === Path.settingsOrganization)
  const periodsAt = organization === -1 ? 0 : organization + 1

  return {
    id: 'settings',
    label: 'Settings',
    description: 'Settings for your whole organization.',
    pathPrefix: Path.settings,
    icon: GearIcon,
    drawer: 'sections',
    railGroup: 'configuration',
    moduleSlug: 'settings',
    sections: [
      {
        label: 'Settings',
        items: [
          ...pageItems.slice(0, periodsAt),
          {
            label: 'Periods',
            to: Path.settingsPeriods,
            description: 'Fiscal years, pay schedules and holiday calendars.',
            icon: CalendarIcon,
          },
          ...pageItems.slice(periodsAt),
          {
            label: 'Credentials',
            to: Path.settingsCredentials,
            description: "Connects Arvoke to your organization's Microsoft 365.",
            icon: KeyIcon,
            visible: credentialsAvailable,
          },
        ],
      },
    ],
  }
}

function navigationModules(settingsModule: AppNavigationModule): AppNavigationModule[] {
  return [
    ARVOKE_AI_MODULE,
    TABLES_MODULE,
    AUTOMATIONS_MODULE,
    INTEGRATIONS_MODULE,
    settingsModule,
    USERS_MODULE,
    ME_MODULE,
  ]
}

/**
 * Produces the navigation model consumed by the sidebar, module drawer, app
 * header, and global page search, so they cannot drift apart.
 *
 * `enabledModuleSlugs` is the set of catalog modules enabled for the tenant;
 * modules outside it are dropped. Pass `null` while it is unknown, so no
 * module flashes in before it loads.
 *
 * `credentialsAvailable` shows Settings > Credentials only where Microsoft 365
 * connections are configured for the environment.
 */
export function createAppNavigation(
  settingsPages: readonly SettingsPage[] = [],
  enabledModuleSlugs: ReadonlySet<string> | null,
  { credentialsAvailable = true }: { credentialsAvailable?: boolean } = {}
): readonly AppNavigationModule[] {
  const isEnabled = (module: AppNavigationModule) =>
    module.moduleSlug === undefined ||
    (enabledModuleSlugs !== null && enabledModuleSlugs.has(module.moduleSlug))

  return navigationModules(
    buildSettingsModule(settingsPages, credentialsAvailable, enabledModuleSlugs)
  ).filter(isEnabled)
}

export function getVisibleNavigationSections(
  module: AppNavigationModule
): readonly AppNavigationSection[] {
  return module.sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.visible !== false),
    }))
    .filter((section) => section.items.length > 0)
}

/** Flattens visible navigation leaves into the client-side Pages search index. */
export function deriveSearchableAppPages(
  modules: readonly AppNavigationModule[]
): SearchableAppPage[] {
  const pagesByPath = new Map<string, SearchableAppPage>()

  for (const module of modules) {
    for (const section of getVisibleNavigationSections(module)) {
      for (const item of section.items) {
        if (item.searchable === false || pagesByPath.has(item.to)) continue

        pagesByPath.set(item.to, {
          id: `${module.id}:${item.to}`,
          name: item.label,
          module: module.label,
          path: item.to,
        })
      }
    }
  }

  return [...pagesByPath.values()]
}

export function isAppModuleId(value: string | null): value is AppModuleId {
  return value !== null && APP_MODULE_IDS.some((moduleId) => moduleId === value)
}

/**
 * The section that owns a route, independent of the user's permissions, so a
 * page-level no-access screen still sits under the right header and rail item.
 */
function getAppModuleDefinitionForPath(pathname: string): AppNavigationModule | undefined {
  return getAppModuleForPath(navigationModules(buildSettingsModule([])), pathname)
}

export function getAppModuleIdForPath(pathname: string): AppModuleId | undefined {
  return getAppModuleDefinitionForPath(pathname)?.id
}

export function getAppModuleLabelForPath(pathname: string): string | undefined {
  return getAppModuleDefinitionForPath(pathname)?.label
}

/**
 * Where a route sits in the navigation, independent of the user's
 * permissions: the module and, when one matches, the drawer item, e.g.
 * `['Automations', 'Runs']`. The AI Assistant's page context uses it.
 */
export function getNavigationTrailForPath(pathname: string): string[] {
  const module = getAppModuleDefinitionForPath(pathname)
  if (!module) return []

  const matches = (to: string) =>
    pathname === to ||
    pathname.startsWith(to.endsWith('/') ? to : `${to}/`) ||
    (to.endsWith('/') && pathname === to.slice(0, -1))
  const item = module.sections
    .flatMap((section) => section.items)
    .filter((candidate) => matches(candidate.to))
    .sort((a, b) => b.to.length - a.to.length)[0]

  return item && item.label !== module.label ? [module.label, item.label] : [module.label]
}

export function getAppModuleForPath(
  modules: readonly AppNavigationModule[],
  pathname: string
): AppNavigationModule | undefined {
  return modules.find(
    (module) => pathname === module.pathPrefix || pathname.startsWith(`${module.pathPrefix}/`)
  )
}
